// src/components/magdrops/QuoteResults.tsx
import { Footer, Header } from "@/components/shared";
import { CheckCircle, Mail } from "lucide-react";

interface Package {
  id: string;
  title: string;
  subtitle: string;
  price: string;
  timeline: string;
  description: string;
  features: string[];
  perfect_for: string;
  badge?: string;
}

interface ProjectDetails {
  businessType: string;
  audience: string;
  pages: string;
  timeline: string;
  printRun: string;
  distribution: string;
}

interface QuoteResultsProps {
  selectedPackage: Package;
  projectDetails: ProjectDetails;
  onReset: () => void;
}

export default function QuoteResults({
  selectedPackage,
  projectDetails,
  onReset,
}: QuoteResultsProps) {
  // Format business type for display
  const formatBusinessType = (type: string) => {
    const typeMap: { [key: string]: string } = {
      property: "Property Development",
      hospitality: "Hotels & Restaurants",
      retail: "Retail & High Street",
      professional: "Professional Services",
      local: "Local Business",
      other: "Other",
    };
    return typeMap[type] || type;
  };

  // Create comprehensive pre-filled email content
  const emailSubject = `MagDrops Discovery Call Request - ${selectedPackage.title}`;
  const emailBody = `Hi MagDrops Team,

I've completed your quote builder and would like to schedule a discovery call to discuss my magazine project.

=== PROJECT OVERVIEW ===
Package Selected: ${selectedPackage.title} (${selectedPackage.timeline}, ${
    selectedPackage.price
  })
Business Type: ${formatBusinessType(projectDetails.businessType)}
Target Audience: ${projectDetails.audience || "Not specified"}

=== PROJECT SPECIFICATIONS ===
Page Count: ${
    projectDetails.pages ? projectDetails.pages + " pages" : "To be confirmed"
  }
Timeline Priority: ${
    projectDetails.timeline
      ? projectDetails.timeline.charAt(0).toUpperCase() +
        projectDetails.timeline.slice(1)
      : "Standard"
  }
Estimated Print Run: ${projectDetails.printRun || "To be discussed"}

=== DISTRIBUTION STRATEGY ===
${projectDetails.distribution || "To be discussed during call"}

=== NEXT STEPS ===
I'm ready to move forward with this project and would appreciate:
- A discovery call to refine the brief
- Transparent print costs for my specific requirements  
- Confirmation of timeline and deliverables

Please let me know your availability for a discovery call.

Best regards,`;

  const mailtoUrl = `mailto:magdrops@thecontentgroup.co?subject=${encodeURIComponent(
    emailSubject
  )}&body=${encodeURIComponent(emailBody)}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50">
      <Header />

      <div className="pt-20 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl p-6 sm:p-12 shadow-lg border border-white/50 mb-8">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center bg-gradient-to-r from-green-100 to-blue-100 text-green-700 rounded-full px-4 sm:px-6 py-2 mb-6 sm:mb-8">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-semibold text-sm sm:text-base">
                  Quote ready
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-8 text-slate-900">
                Your custom magazine quote
              </h1>
            </div>

            {/* Project Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 border border-blue-100">
              <h3 className="text-lg font-semibold mb-4 text-slate-900">
                Your project summary
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-slate-500">Package</div>
                  <div className="font-medium text-slate-900">
                    {selectedPackage.title}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Timeline</div>
                  <div className="font-medium text-slate-900">
                    {selectedPackage.timeline}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Business type</div>
                  <div className="font-medium text-slate-900">
                    {formatBusinessType(projectDetails.businessType)}
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Page count</div>
                  <div className="font-medium text-slate-900">
                    {projectDetails.pages || "TBC"} pages
                  </div>
                </div>
                {projectDetails.timeline && (
                  <div>
                    <div className="text-slate-500">Priority</div>
                    <div className="font-medium text-slate-900 capitalize">
                      {projectDetails.timeline}
                    </div>
                  </div>
                )}
                {projectDetails.printRun && (
                  <div>
                    <div className="text-slate-500">Print run</div>
                    <div className="font-medium text-slate-900">
                      {projectDetails.printRun}
                    </div>
                  </div>
                )}
              </div>

              {projectDetails.audience && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="text-slate-500 text-sm mb-1">
                    Target audience
                  </div>
                  <div className="font-medium text-slate-900 text-sm">
                    {projectDetails.audience}
                  </div>
                </div>
              )}

              {projectDetails.distribution && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="text-slate-500 text-sm mb-1">
                    Distribution plan
                  </div>
                  <div className="font-medium text-slate-900 text-sm">
                    {projectDetails.distribution}
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Display */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-2">
                  {selectedPackage.price}
                </div>
                <div className="text-sm text-slate-600">Service fee</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-900 mb-2">
                  + Print
                </div>
                <div className="text-sm text-slate-600">Transparent costs</div>
              </div>
            </div>

            {/* Primary CTA */}
            <div className="space-y-4 mb-6 sm:mb-8">
              <a
                href={mailtoUrl}
                className="block bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg shadow-blue-600/25 text-center"
              >
                <Mail className="w-5 h-5 inline mr-2" />
                Book discovery call
              </a>

              <p className="text-slate-600 text-sm max-w-xl mx-auto text-center">
                We'll discuss your project details, provide exact print costs,
                and outline the timeline for your {selectedPackage.title}{" "}
                magazine.
              </p>
            </div>

            {/* Next Steps */}
            <div className="border-t border-slate-200 pt-6 sm:pt-8">
              <h4 className="text-lg font-semibold mb-6 text-slate-900 text-center">
                Next steps
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold mb-3">
                    1
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 mb-1">
                      Discovery call
                    </div>
                    <div className="text-slate-600">
                      Refine brief & confirm timeline
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold mb-3">
                    2
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 mb-1">
                      Print quote
                    </div>
                    <div className="text-slate-600">
                      Transparent costs for your run
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold mb-3">
                    3
                  </div>
                  <div>
                    <div className="font-medium text-slate-900 mb-1">
                      Production start
                    </div>
                    <div className="text-slate-600">
                      Week 1: Strategy & planning
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Secondary Actions */}
          <div className="text-center">
            <button
              onClick={onReset}
              className="border border-slate-300 hover:bg-slate-100 text-slate-700 px-6 sm:px-8 py-3 rounded-lg font-medium transition-colors"
            >
              Start new quote
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
